import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/formatUtils';
import RarityBadge from '@/components/RarityBadge';
import OfficialTag from '@/components/OfficialTag';
import arrowIcon from '@/assets/chevron_right.svg';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const ProfileItemCard = ({
  item,
  statusText,
  statusLabel,
  className = '',
  onClick,
  orderId,
  saleId
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    console.log(item);
    if (onClick) {
      // If orderId or saleId is provided, navigate to the specific detail page
      if (orderId) {
        navigate(`/me/orders?orderId=${orderId}`);
      } else if (saleId) {
        navigate(`/me/sales?saleId=${saleId}`);
      } else {
        onClick(item);
      }
    }
  };

  // Function to determine the property display label and value
  const getGradeText = () => {
    if (item.psa) {
      return {
        label: t('me.profile.info.psa_grade'),
        value: item.psa.replace('PSA_', 'PSA')
      };
    }
    
    if (item.bundledQuantity) {
      return {
        label: t('me.profile.info.quantity'),
        value: `${item.bundledQuantity} ${item.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit')}`
      };
    }
    
    if (item.condition) {
      return {
        label: t('me.profile.info.condition'),
        value: t(`sell.product_info.conditions.${item.condition.toLowerCase()}`)
      };
    }
    
    return {
      label: t('me.profile.info.type'),
      value: '-'
    };
  };

  const gradeInfo = getGradeText();
  
  return (
    <div 
      className={`p-4 bg-white rounded-md border border-[#dddddd] flex flex-col gap-4 ${className}`}
    >
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#111111]">{statusText}</div>
        <div 
          className="flex items-center cursor-pointer"
          onClick={handleClick}
        >
          <div className="text-sm font-bold text-[#111111]">{statusLabel}</div>
          <img src={arrowIcon} className='h-5 w-5' />
        </div>
      </div>
      
      <div className="flex gap-4">
        <div className="w-[168px] h-[168px] bg-white rounded overflow-hidden flex items-center justify-center">
          <img 
            src={getProductThumbnailUrl(item.product?.images?.[0]?.url)} 
            alt={item.product?.localizedName || 'Product'} 
            className="w-full h-full object-contain mix-blend-darken"
          />
        </div>
        
        <div className="flex-1 flex flex-col gap-6">
          <div>
            <div className="min-w-0">
              {item.isOfficial && (
                <OfficialTag className="align-middle mr-1.5" />
              )}
              <Link 
                to={item.type === 'NON_PSA_SINGLE_CARD' ? `/listing/${item.id}` : `/card/${item.product.id}`}
                className="inline align-middle text-sm text-[#111111] hover:underline font-medium whitespace-normal break-words min-w-0"
              >
                {item.product?.localizedRawName || item.product?.localizedName || 'Product Name'}
                {(!item.bundledQuantity && item.product?.rarityInfo?.sub?.name) ? `(${item.product.rarityInfo.sub.name})` : ''}
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {!item.bundledQuantity && item.product?.rarityInfo?.main && (
                <RarityBadge rarity={item.product.rarityInfo.main} />
              )}
              {(item.product?.set?.code || (item.product?.rawCode && item.type !== 'CARD_BOX')) && (
                <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                  <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                    {item.type === 'CARD_BOX' ? item.product?.set?.code : (item.product?.set?.code && item.product?.rawCode ? `${item.product.set.code} ${item.product.rawCode}` : item.product?.set?.code || item.product?.rawCode)}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-col gap-[5px]">
            <div className="flex gap-2">
              <div className="w-[100px] text-sm text-[#aaaaaa]">{gradeInfo.label}</div>
              <div className="flex-1 text-right text-sm text-[#111111]">{gradeInfo.value}</div>
            </div>
            <div className="flex gap-2">
              <div className="w-[100px] text-sm text-[#aaaaaa]">{t('me.profile.info.price')}</div>
              <div className="flex-1 text-right text-sm text-[#111111]">{formatCurrency(item.price || 0)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileItemCard; 
